import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import {
  Users, UserX, ArrowRight, CheckCircle, AlertTriangle,
  PoundSterling, Shield, Clock, Briefcase, BookOpen
} from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';

const AGENT_FEES = {
  UK: {
    currency: '£', 
    agent: { label: 'Estate Agent', range: '1% - 3% + VAT', example: '£2,500 - £7,500 on a £250,000 property' },
    solicitor: { label: 'Solicitor/Conveyancer', range: '£500 - £1,500 + VAT', example: 'Required for both routes' },
    surveyor: { label: 'Surveyor', range: '£300 - £700', example: 'Recommended for buyers' },
    mortgage_advisor: { label: 'Mortgage Advisor', range: 'Free - £500', example: 'Many are commission-based (free to you)' },
  },
  IN: {
    currency: '₹',
    agent: { label: 'Property Broker', range: '1% - 2%', example: '₹50,000 - ₹2,00,000 on a ₹1 Cr property' },
    solicitor: { label: 'Property Lawyer', range: '₹10,000 - ₹50,000', example: 'For agreement drafting & registration' },
    surveyor: { label: 'Property Valuer', range: '₹5,000 - ₹25,000', example: 'Bank-approved valuers for loans' },
    mortgage_advisor: { label: 'Loan Agent', range: '0.5% - 1%', example: 'Some banks offer direct processing' },
  },
  CA: {
    currency: '$',
    agent: { label: 'Real Estate Agent', range: '3% - 5%', example: '$15,000 - $25,000 on a $500,000 property' },
    solicitor: { label: 'Real Estate Lawyer', range: '$500 - $2,000', example: 'Required for closing' },
    surveyor: { label: 'Home Inspector', range: '$300 - $600', example: 'Highly recommended before purchase' },
    mortgage_advisor: { label: 'Mortgage Broker', range: 'Free - $1,000', example: 'Usually paid by lender' },
  },
};

const GUIDANCE = {
  seller: {
    with_agent: [
      'Agent handles viewings, negotiations, and marketing',
      'Professional photography and listing descriptions',
      'Access to agent\'s buyer network',
      'Agent manages the entire sales process',
      'You pay agent commission on completion',
    ],
    without_agent: [
      'List your property free on Shopperden with 3D photos',
      'Use our AI valuation to set the right price',
      'Manage viewings directly with interested buyers',
      'Use Shopperden messaging for secure communication',
      'Save thousands in agent commission fees',
      'Still need a solicitor/conveyancer for legal work',
    ],
  },
  buyer: {
    with_agent: [
      'Agent finds properties matching your criteria',
      'Agent negotiates price on your behalf',
      'Access to off-market properties',
      'Agent guides you through the buying process',
      'Buyer agent fees vary by country (sometimes free)',
    ],
    without_agent: [
      'Search properties directly on Shopperden',
      'Use AI valuation to check if the price is fair',
      'Book viewings directly with sellers',
      'Use our Mortgage Guide for finance pre-qualification',
      'Get a solicitor and surveyor through our directory',
      'Save money by negotiating directly',
    ],
  },
  tenant: {
    with_agent: [
      'Agent finds rental properties for you',
      'Agent handles viewing arrangements',
      'Agent negotiates lease terms',
      'Tenant fees may apply (check local regulations)',
      'In UK: Tenant Fees Act limits what agents can charge',
    ],
    without_agent: [
      'Search rentals directly on Shopperden',
      'Message landlords directly — you stay in control',
      'Handle viewings and lease on your own schedule',
      'Use our secure in-app messaging',
      'Review lease agreements with a solicitor if needed',
    ],
  },
};

const AgentChoicePanel = ({ userRole = 'buyer', onChooseAgent, onChooseWithout }) => {
  const { country } = useCountry();
  const [choice, setChoice] = useState(null);
  const fees = AGENT_FEES[country] || AGENT_FEES.UK;
  const guidance = GUIDANCE[userRole] || GUIDANCE.buyer;

  const feeRows = useMemo(() =>
    Object.entries(fees).filter(([k]) => k !== 'currency'),
    [fees]
  );

  const headerText = useMemo(() => {
    if (userRole === 'seller') return 'Choose whether to sell with a professional agent or manage the sale yourself with Shopperden\'s tools.';
    if (userRole === 'tenant') return 'Choose whether to rent through a letting agent or find properties directly.';
    return 'Choose whether to buy with a professional agent or search and negotiate directly.';
  }, [userRole]);

  return (
    <div data-testid="agent-choice-panel">
      {/* Choice Header */}
      <div className="text-center mb-8">
        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] font-semibold mb-2">How Would You Like to Proceed?</h2>
        <p className="text-slate-500 text-sm max-w-xl mx-auto">
          {headerText}
        </p>
      </div>

      {/* Two Options */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* With Agent */}
        <div
          onClick={() => { setChoice('with_agent'); onChooseAgent(); }}
          className={`cursor-pointer border-2 p-6 transition-all hover:shadow-lg ${
            choice === 'with_agent' ? 'border-[#E07A3A] bg-[#FFF7ED]' : 'border-slate-200 hover:border-[#E07A3A]/40'
          }`}
          data-testid="choice-with-agent"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 flex items-center justify-center ${choice === 'with_agent' ? 'bg-[#E07A3A] text-white' : 'bg-slate-100 text-slate-500'}`}>
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#0F172A]">With Agent</h3>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Full professional support</p>
            </div>
          </div>
          <ul className="space-y-2 mb-4">
            {guidance.with_agent.map((item, i) => (
              <li key={`wa-${item.slice(0, 15)}`} className="flex items-start gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
          <div className="bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs text-amber-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              <strong>Typical cost:</strong> {fees.agent.range} ({fees.agent.example})
            </p>
          </div>
        </div>

        {/* Without Agent */}
        <div
          onClick={() => { setChoice('without_agent'); onChooseWithout(); }}
          className={`cursor-pointer border-2 p-6 transition-all hover:shadow-lg ${
            choice === 'without_agent' ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-200 hover:border-emerald-400/40'
          }`}
          data-testid="choice-without-agent"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 flex items-center justify-center ${choice === 'without_agent' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
              <UserX className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#0F172A]">Without Agent</h3>
              <p className="text-xs text-emerald-600 uppercase tracking-wider font-semibold">With Shopperden guidance</p>
            </div>
          </div>
          <ul className="space-y-2 mb-4">
            {guidance.without_agent.map((item, i) => (
              <li key={`wo-${item.slice(0, 15)}`} className="flex items-start gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
          <div className="bg-emerald-50 border border-emerald-200 p-3">
            <p className="text-xs text-emerald-700 flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              <strong>Potential savings:</strong> {fees.agent.example.split('on')[0]} in agent fees
            </p>
          </div>
        </div>
      </div>

      {/* Fee Comparison Table */}
      <div className="luxury-card p-6 mb-6" data-testid="fee-comparison">
        <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] mb-4 flex items-center gap-2">
          <PoundSterling className="h-5 w-5 text-[#E07A3A]" />
          Professional Fee Guide ({country})
        </h3>
        <p className="text-xs text-slate-400 mb-4">Typical fees charged by professionals in your region. These are industry standard rates — similar to what you'd see on Rightmove, Zoopla, or 99acres.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-2 text-xs text-slate-400 uppercase tracking-wider font-semibold">Professional</th>
                <th className="text-left py-3 px-2 text-xs text-slate-400 uppercase tracking-wider font-semibold">Typical Fee</th>
                <th className="text-left py-3 px-2 text-xs text-slate-400 uppercase tracking-wider font-semibold hidden sm:table-cell">Note</th>
              </tr>
            </thead>
            <tbody>
              {feeRows.map(([key, fee]) => (
                <tr key={key} className="border-b border-slate-50">
                  <td className="py-3 px-2 font-medium text-[#0F172A]">{fee.label}</td>
                  <td className="py-3 px-2 text-[#E07A3A] font-semibold">{fee.range}</td>
                  <td className="py-3 px-2 text-slate-400 text-xs hidden sm:table-cell">{fee.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action buttons based on choice */}
      {choice && (
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-testid="choice-actions">
          {choice === 'with_agent' ? (
            <Button
              onClick={onChooseAgent}
              className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-8 py-5 text-sm tracking-widest uppercase font-semibold"
              data-testid="btn-find-professionals"
            >
              <Users className="h-4 w-4 mr-2" />
              Browse & Choose Professionals <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={onChooseWithout}
              className="bg-emerald-600 text-white hover:bg-emerald-700 px-8 py-5 text-sm tracking-widest uppercase font-semibold"
              data-testid="btn-start-guidance"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Start with Shopperden Guidance <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentChoicePanel;
